import axios from 'axios';
import { AUTH_URL } from '../config/apiUrls';
import { getAccessToken } from '../utils/authTokens';
import type { PrivacyPolicy, PrivacyPolicyAcceptedStatus } from '../types/privacy-policy';

const api = axios.create({
  baseURL:
    process.env.REACT_APP_AUTH_URL ||
    process.env.NEXT_PUBLIC_AUTH_API_URL ||
    AUTH_URL ||
    'http://localhost:3000',
});

// Helper to set auth header
const authHeaders = (token?: string) => {
  const effectiveToken = token || getAccessToken() || '';
  return effectiveToken
    ? { headers: { Authorization: `Bearer ${effectiveToken}` } }
    : {};
};

export const PrivacyPolicyService = {
  // 1. Check if accepted
  checkAccepted: async (
    version: number | string = 'current',
    token?: string
  ): Promise<PrivacyPolicyAcceptedStatus> => {
    const res = await api.get<PrivacyPolicyAcceptedStatus>(
      `/privacy-policy/${version}/accepted`,
      authHeaders(token)
    );
    return res.data;
  },

  // 2. Get current policy content
  getCurrent: async (token?: string): Promise<PrivacyPolicy> => {
    const res = await api.get<PrivacyPolicy>(
      '/privacy-policy/current',
      authHeaders(token)
    );
    return res.data;
  },

  // 3. Accept policy
  accept: async (
    version: number | string = 'current',
    token?: string,
    value: string = ''
  ): Promise<PrivacyPolicyAcceptedStatus> => {
    const res = await api.post<PrivacyPolicyAcceptedStatus>(
      `/privacy-policy/${version}/accept`,
      { value },
      authHeaders(token)
    );
    return res.data;
  },

  // 4. Reject policy
  reject: async (
    version: number | string = 'current',
    token?: string,
    reason?: string
  ): Promise<PrivacyPolicyAcceptedStatus> => {
    const res = await api.post<PrivacyPolicyAcceptedStatus>(
      `/privacy-policy/${version}/reject`,
      { reason },
      authHeaders(token)
    );
    return res.data;
  },

  // 5. Get all policy versions (Admin / Public list)
  getAll: async (token?: string): Promise<PrivacyPolicy[]> => {
    const res = await api.get<PrivacyPolicy[]>('/privacy-policy', authHeaders(token));
    return Array.isArray(res.data) ? res.data : [];
  },

  // 6. Get policy by version number
  getByVersion: async (
    version: number | string,
    token?: string
  ): Promise<PrivacyPolicy> => {
    const res = await api.get<PrivacyPolicy>(
      `/privacy-policy/${version}`,
      authHeaders(token)
    );
    return res.data;
  },

  // 7. Create a new privacy policy version (Superadmin / Lib Admin)
  create: async (
    payload: import('../types/privacy-policy').CreatePrivacyPolicyPayload,
    token?: string
  ): Promise<PrivacyPolicy> => {
    const res = await api.post<PrivacyPolicy>(
      '/privacy-policy/post',
      payload,
      authHeaders(token)
    );
    return res.data;
  },

  // 8. Make a specific version the current active policy
  makeCurrent: async (
    version: number | string,
    token?: string
  ): Promise<PrivacyPolicy> => {
    const res = await api.post<PrivacyPolicy>(
      `/privacy-policy/${version}/make-current`,
      {},
      authHeaders(token)
    );
    return res.data;
  },

  // 9. Update an existing policy version
  update: async (
    version: number | string,
    payload: import('../types/privacy-policy').UpdatePrivacyPolicyPayload,
    token?: string
  ): Promise<PrivacyPolicy> => {
    const res = await api.put<PrivacyPolicy>(
      `/privacy-policy/${version}`,
      payload,
      authHeaders(token)
    );
    return res.data;
  },

  // 10. Delete a policy version
  delete: async (
    version: number | string,
    token?: string
  ): Promise<{ success?: boolean; message?: string }> => {
    const res = await api.delete(
      `/privacy-policy/${version}`,
      authHeaders(token)
    );
    return res.data;
  },
};

export { PrivacyPolicyService as privacyPolicyService };
export default PrivacyPolicyService;
